import React, { useState, useEffect } from 'react';
import { Card, Form, Row, Col, Select, Button, Spin, DatePicker } from 'antd';
import { errHandler } from '../../helper/helper';
import { generateAllDisposeSummaryReport } from "./LoanService";
import generateDistrictDisposeExcel from '../../Utils/Loan/DistrictWiseDisposeExcel';
import { disabledDate } from "../../Utils/Util";
import BranchLocationMultipleSelector from '../BranchLocationMultipleSelector';

const { Option } = Select;
const { RangePicker } = DatePicker;

const DistrictWiseDisposeSummary = ({
    loginPlaceId,
    districtData,
    districtName,
    loanProducts,
    divisionalId,
    branchId
}) => {
    const [loading, setLoading] = useState(false);
    const [selectedDistricts, setSelectedDistricts] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [locationData, setLocationData] = useState({});


    useEffect(() => {
        if (loginPlaceId === "4" && districtName && districtData.length > 0) {
            const userDistrict = districtData.find(d => d.districtName === districtName);
            if (userDistrict) {
                setSelectedDistricts([userDistrict.districtId]);
            }
        }
    }, [loginPlaceId, districtName, districtData]);

    const handleDistrictChange = (values) => {
        if (values.includes("ALL")) {
            if (loginPlaceId === "4") {
                const userDistrict = districtData.find(d => d.districtName === districtName);
                if (userDistrict) setSelectedDistricts([userDistrict.districtId]);
            } else {
                setSelectedDistricts(districtData.map(d => d.districtId));
            }
        } else {
            setSelectedDistricts(values);
        }
    };

    const handleProductsChange = (values) => {
        if (values.includes("ALL")) {
            if (selectedProducts.length === loanProducts.length) {
                setSelectedProducts([]);
            } else {
                setSelectedProducts(loanProducts.map(p => p.productId));
            }
        } else {
            setSelectedProducts(values);
        }
    };

    const handleDateRangeChange = (dates) => {
        if (dates && dates[0] && dates[1]) {
            setStartDate(dates[0]);
            setEndDate(dates[1]);
        } else {
            setStartDate(null);
            setEndDate(null);
        }
    };

    const generateReport = async () => {
        if (!startDate || !endDate) {
            errHandler("Please select date range");
            return;
        }
        setLoading(true);

        try {
            const payload = {
                selectedDate: startDate.format("YYYY-MM-DD"),
                endDate: endDate.format("YYYY-MM-DD"),
                branchIdList: loginPlaceId === "1" ? [branchId] : locationData.branchIds,
                productIdList: selectedProducts.length === 0 ? null : selectedProducts
            };

            const response = await generateAllDisposeSummaryReport(payload);
            console.log("API Response:", response);

            let rawData = response?.data || (Array.isArray(response) ? response : []);

            if (rawData.length === 0) {
                errHandler("No disposal records found");
                return;
            }

            let filteredData = rawData;
            if (selectedProducts.length > 0) {
                const selectedNames = selectedProducts.map(id => {
                    const p = loanProducts.find(prod => prod.productId === id);
                    return p ? p.productName.trim().toLowerCase() : "";
                }).filter(Boolean);

                filteredData = rawData.filter(item => {
                    const itemName = (item.productName || "").trim().toLowerCase();
                    return selectedNames.some(name => itemName.includes(name));
                });
            }

            if (response.length === 0) {
                errHandler("No matching records for selected product(s)");
                return;
            }



            const selectedDistrictNames = districtData
                .filter(d => selectedDistricts.includes(d.districtId))
                .map(d => d.districtName);

            let productHeader = "All Products";
            if (selectedProducts.length === 1) {
                const prod = loanProducts.find(p => p.productId === selectedProducts[0]);
                productHeader = prod ? `${prod.productCode} - ${prod.productName}` : "Selected Product";
            } else if (selectedProducts.length > 1) {
                productHeader = `${selectedProducts.length} Products Selected`;
            }

            await generateDistrictDisposeExcel(
                rawData,
                startDate.format("YYYY-MM-DD"),
                endDate.format("YYYY-MM-DD"),
                selectedDistrictNames,
                loginPlaceId,
                productHeader
            );

        } catch (error) {
            errHandler("Failed to generate report");
        } finally {
            setLoading(false);
        }
    };

    const isGenerateDisabled = loading || !startDate || !endDate || (loginPlaceId === 1 && locationData.branchIds.length === 0);

    return (
        <div className="panel-container">
            <Card hoverable className="inner-card">
                <Form layout="vertical">
                    <BranchLocationMultipleSelector loginPlaceId={loginPlaceId} districtData={districtData} districtName={districtName} divisionalId={divisionalId} branchId={branchId} onChange={(setLocationData)} />

                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Form.Item label="Select Products">
                                <Select
                                    mode="multiple"
                                    placeholder="Select products"
                                    style={{ width: '100%' }}
                                    value={selectedProducts}
                                    onChange={handleProductsChange}
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().includes(input.toLowerCase())
                                    }
                                    maxTagCount="responsive"
                                >
                                    <Option key="ALL" value="ALL">
                                        {selectedProducts.length === loanProducts.length ? 'Deselect All' : 'Select All Products'}
                                    </Option>
                                    {loanProducts.map(p => (
                                        <Option key={p.productId} value={p.productId}>
                                            {p.productCode} - {p.productName}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Form.Item label="Select Date Range">
                                <RangePicker
                                    placeholder={['Start Date', 'End Date']}
                                    style={{ width: '100%' }}
                                    value={startDate && endDate ? [startDate, endDate] : []}
                                    onChange={handleDateRangeChange}
                                    format="YYYY-MM-DD"
                                    disabledDate={disabledDate}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Form.Item className="generate-btn-outline">
                                <Button
                                    type="primary"
                                    onClick={generateReport}
                                    disabled={isGenerateDisabled}
                                    className="generate-btn"
                                    size="large"
                                    block
                                >
                                    {loading ? <Spin /> : 'Generate Report'}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Card>
        </div>
    );
};

export default DistrictWiseDisposeSummary;